import moment from "moment"
import { ENUM } from "../constants/enum"

export const displayDefault = (value = '') => {
  if (!value) {
    return '-'
  }
  return value
}

export const displayDate = (value: string | Date = '', option: any = {}) => {
  if (!value || moment(value).format('DD/MM/YYYY') === 'Invalid date') {
    return '-'
  }
  if (option.isTime) {
    return moment(value).format('DD/MM/YYYY, HH:mm')
  }
  if (option.isOnlyTime) {
    return moment(value).format('HH:mm')
  }
  return moment(value).format('DD/MM/YYYY')

}

export const displayFullname = (firstname = '', lastname = ''): string => {
  return `${firstname} ${lastname}`
}

export const convertMarkdownToHtml = (message = '', option: any = { isEndStream: false }) => {
  const markDownList = [
    {
      // block code: ```text```
      match: /```(\w+)?\n([\s\S]*?)```/g,
      replaceWith: (message: string, match: any) => {
        return message.replace(match, (match, lang, code) => {
          return `<pre><code class="language-${lang}">${escapeHtml(code)}</code></pre>`;
        });
      },
    },
    {
      // code inline: `text`
      match: /`([^`]+)`/g,
      replaceWith: (message: string, match: any) => {
        return message.replace(match, (match, p1) => {
          return `<code style="background-color: #f5f2f0;border-radius: 0.25rem;font-size: 0.875em;font-weight: 500;padding: 0.15rem 0.3rem;">${escapeHtml(p1)}</code>`;
        });
      },
    },
    {
      // h3: ### text
      match: /### (.+)\n/g,
      replaceWith: (message: string, match: any) => {
        return message.replace(match, (match, p1) => {
          return `<h3 style="font-size: 1.25em;font-weight: 600;">${p1}</h3>`;
        });
      },
    },
    {
      // bold: **text**
      match: /\*\*([^*]+)\*\*/g,
      replaceWith: (message: string, match: any) => {
        return message.replace(match, (match, p1) => {
          return `<strong>${p1}</strong>`;
        });
      },
    },
    {
      // link: [text](url)
      match: /\[([^\]]+)]\((https?:\/\/[^\s)]+)\)/g,
      replaceWith: (message: string, match: any) => {
        return message.replace(match, (match, p1, p2) => {
          return `<a style="color: #7ab7ff" href="${p2}" target="_blank" rel="noopener noreferrer">${p1}</a>`;
        });
      },
    },
    {
      // table: | h1 | h2 |\n|---|---|\n| b1 | b2 |
      isEndStream: true,
      match: /^(?!```)(\|.+\|\n\|[-|: ]+\|\n(?:\|.+\|\n?)+)/gm,
      replaceWith: (message: string, match: any) => {
        return message.replace(match, (tableMarkdown) => {

          const lines = tableMarkdown.trim().split('\n');

          // Remove the separator line
          const [headerLine, , ...rows] = lines;

          const headers = headerLine
            .split('|')
            .map(h => h.trim())
            .filter(Boolean);

          const headerHTML = headers.map(h => `<th style="border: 1px solid black;">${h}</th>`).join('');

          const rowsHTML = rows.map(row => {
            const cells = row
              .split('|')
              .map(cell => cell.trim())
              .filter(Boolean);
            return `<tr>${cells.map(c => `<td style="border: 1px solid black;">${c}</td>`).join('')}</tr>`;
          }).join('\n');

          return `<table style="border: 1px solid black; border-collapse: collapse;">\n<thead><tr>${headerHTML}</tr></thead>\n<tbody>\n${rowsHTML}\n</tbody>\n</table>`;
        });
      },
    },
  ];

  markDownList.forEach((e) => {
    if (!e.isEndStream) {
      message = e.replaceWith(message, e.match);
    }
    if (e.isEndStream && option.isEndStream) {
      message = e.replaceWith(message, e.match);
    }
  });

  return message
}

export const escapeHtml = (code = '') => {
  return code
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export const getChatbotStatus = (value: string) => {
  switch (value) {
    case ENUM.CHATBOT_STATUS.DRAFT:
      return 'Draft'
    case ENUM.CHATBOT_STATUS.LIVE:
      return 'Live'
    default: return ''
  }
}

export const displayMessageFileList = (list: []) => {
  const { $services } = useNuxtApp();
  const presetService = $services.presetController()
  return Promise.all(list.map(async (e: any) => {
    e.filesList = await Promise.all(
      e.filesList.map(async (ef: any) => {
        ef.displayFile = URL.createObjectURL(
          await presetService.getPresetFile(ef.filePath)
        );
        return ef;
      })
    );
    return e;
  }));
}

export const displayFileList = (list: [], option: any = {}) => {
  const { $services } = useNuxtApp();
  const presetService = $services.presetController()
  return Promise.all(list.map(async (e: any) => {
    if (e[option.source]) {
      e[option.output] = URL.createObjectURL(
        await presetService.getPresetFile(e[option.source])
      );
    }
    else if (option.default) {
      e[option.output] = option.default
    }
    return e;
  }));
}

export const displayNumberWithComma = (number: number | string) => {
  if (typeof number === 'string') {
    return parseFloat(number || '0').toLocaleString();
  }
  return number.toLocaleString();
}

export const displayReportMessageType = (value: string) => {
  switch (value) {
    case 'title-generation':
      return 'Create Conversation';
    case 'message-preview':
      return 'Preview Message';
    case 'message-creation':
      return 'Create Message';
    case 'message-append':
      return 'Append Message';
    default:
      return '-';
  }
}


export const displayFilename = (filePath: string = ''): string => {
  if (!filePath) return '';
  const parts = filePath.split('/');
  const filename = parts[parts.length - 1] || '';
  
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i;
  
  if (uuidPattern.test(filename)) {
    return filename.substring(37);
  }
  
  return filename;
};